import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

export function SetPlanModal({ open, onClose, fiscalYear }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ costCenterId: '', plannedAmount: '' });
  const [error, setError] = useState(null);

  const costCenters = useQuery({ queryKey: ['finance', 'cost-centers'], queryFn: () => financeApi.listCostCenters({ activeOnly: true }).then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm({ costCenterId: '', plannedAmount: '' }); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: ({ id, body }) => financeApi.setCostCenterPlan(id, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'controlling'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.costCenterId || form.plannedAmount === '') { setError(t('co.error.required')); return; }
    save.mutate({ id: form.costCenterId, body: { fiscalYear, plannedAmount: Number(form.plannedAmount) } });
  }

  return (
    <Modal open={open} onClose={onClose} title={`${t('co.plan.title')} ${fiscalYear}`}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('co.field.costCenter')}</span>
          <select className="input" value={form.costCenterId} onChange={set('costCenterId')} required>
            <option value="">-</option>
            {(costCenters.data || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('co.field.plannedAmount')}</span>
          <input className="input num" type="number" step="0.01" min="0" value={form.plannedAmount} onChange={set('plannedAmount')} required />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
